<template>
  <div
    v-if="event !== undefined && roomActive === true"
    :style="event.branding ? { background: event.branding.tertiaryColor } : { background: '#FFFFFF' }"
  >
    <b-loading :active="!testDataChannelOk && role !== 'proxy'"></b-loading>
    <EventChat
      v-if="role !== 'proxy'"
      ref="eventChat"
      :event="event"
      :is-translator="isTranslator"
      :microphones="microphones"
      :cameras="cameras"
      :current-mic-id="currentMicId"
      :current-cam-id="currentCamId"
      :participants="participants"
      :translators="translators"
      :local-mute="micPaused"
      :lang-white-list="langWhiteList"
      :settings-relay.sync="settingsRelayLang"
      :client-translator="clientTranslator"
      :translator-partners="translatorPartners"
      :is-moderator="isModerator"
      :class="activeOverlay"
      :big.sync="big"
      @click-force-takeover="startForceTakeover"
      @global-mute="muteUser"
      @global-video-mute="muteVidUser"
      @kick="kickUser"
      @stop-presentation="stopPresentation"
      @change-mic="changeMic"
      @change-webcam="changeWebcam"
      @allow-switch-to-host="allowSwitchToHost"
      @switch-to-viewer="switchToViewer"
      @toggle-resolution="toggleResolution"
      @disable-hand-raised="disableRaiseHand"
      @spotlight="setSpotlight"
      @changed-consume-lang="changeConsumeLang"
    />

    <TranslatorOverlay
      v-if="activeOverlay === 'translator'"
      :event="event"
      :translator-ready="clientTranslator.translatorReady"
      :local-mute="micPaused"
      :active-will-take="activeWillTake"
      :client-translator="clientTranslator"
      :active-on-air="activeOnAir"
      :hear-other-translator="hearOtherTranslator"
      :settings-relay-lang="settingsRelayLang"
      :lang-white-list="langWhiteList"
      :relay-lang="relayLang"
      :force-takeover-open="forceTakeoverOpen"
      :auto-detect-force-takeover="autoDetectForceTakeover"
      @toggle-mute="toggleMute"
      @toggle-video-mute="toggleVideoMute"
      @is-ready="translatorIsReady"
      @request-active-handover="requestActiveHandover"
      @request-passive-handover="requestPassiveHandover"
      @accept-active-request="acceptActiveRequest"
      @reject-active-request="rejectActiveRequest"
      @reject-passive-request="rejectPassiveRequest"
      @accept-passive-request="notifyHandoverAccepted"
      @toggle-current-output-lang="toggleCurrentOutputLang"
      @toggle-hear-other-translator="toggleHearOtherTranslator"
      @toggle-floor="toggleFloor"
      @toggle-resolution="toggleResolution"
      @confirm-force-takeover="confirmForceTakeover"
      @abort-force-takeover="abortForceTakeover"
      @click-force-takeover="startForceTakeover"
      @changed-consume-lang="changeConsumeLang"
    />

    <div
      class="top-view-container"
      :class="[big ? 'small' : '', activeOverlay === 'translator' ? 'translator-view' : '']"
    >
      <div class="qonda-watermark">
        <img src="/assets/logos/1c-negative.svg" />
      </div>
      <Conference v-if="isConference" :event="event" />

      <Presentation v-if="isPresentation" />

      <Spotlight v-if="isSpotlight" />

      <ViewerOverlay
        v-if="role === 'viewer'"
        :event="event"
        @leave="leave"
        @request-switch-to-host="requestSwitchToHost"
        @switch-to-host="switchToHost"
      />

      <AdminOverlay v-if="activeOverlay === 'admin'" />

      <HostOverlay
        v-if="activeOverlay === 'host' || activeOverlay === 'hybrid'"
        :event="event"
        :local-mute="micPaused"
        :local-video-mute="vidPaused"
        :req-to-speak-ongoing="reqToSpeakOngoing"
        @leave="leave"
        @toggle-mute="toggleMute"
        @toggle-video-mute="toggleVideoMute"
        @toggle-screen="toggleScreen"
        @toggle-raiseHand="toggleRaiseHand"
        @networkthrottle="applyNetworkThrottle"
        @resetnetworkthrottle="resetNetworkThrottle"
      />
      <div v-if="transcriptionStarted" class="transcription">
        {{ transcriptionResult }}
      </div>
    </div>

    <AudioHandler v-if="audioConnected" :consume-lang="consumeLang" :translator-partners="boothPartners" />
  </div>
</template>
<script>
/**
 * Handles different Layouts based on conference, presentation, spotlight status and inits the audio handler
 * @displayName Room
 */
/* eslint-disable no-unused-vars*/
/* eslint-disable vue/no-unused-components*/
import { auth, db, functions, FieldValue } from "@/firebase";
import AdminOverlay from "./AdminOverlay.vue";
import Conference from "./Conference.vue";
import ViewerOverlay from "./ViewerOverlay.vue";
import HostOverlay from "./HostOverlay.vue";
import TranslatorOverlay from "./TranslatorOverlay.vue";
import RoomClient from "../../mediasoup-client/RoomClient.js";
import deviceInfo from "../../mediasoup-client/deviceInfo.js";
import EventChat from "../EventChat.vue";
import { mapState } from "vuex";
import Presentation from "./Presentation.vue";
import AudioHandler from "./AudioHandler.vue";
import Spotlight from "./Spotlight.vue";
import Logger from "../../mediasoup-client/Logger";

const logger = new Logger("Room.vue");

//Room handling Conference client and different
//appearance overlays depending on the role of the given user
//everything thats same for all clients is handled here - like Chat

export default {
  name: "Room",
  components: {
    ViewerOverlay,
    AdminOverlay,
    Conference,
    HostOverlay,
    TranslatorOverlay,
    EventChat,
    Presentation,
    Spotlight,
    AudioHandler,
  },
  props: {
    //event:Object,
    isTranslator: Boolean,
    isHybridUser: Boolean,
  },
  data() {
    return {
      roomClient: null,
      firestoreParticipants: [],
      firestoreTranslators: [],
      // translatorReady: false,
      activeTranslatorUid: null,
      lastNoToAutoDetect: 0,
      settingsRelayLang: [],
      relayLang: null,
      doForceTakeover: false,
      event: undefined,
      isModerator: false,
      tourSteps: [],
      big: false,
      roomActive: false,
    };
  },
  beforeMount() {
    window.addEventListener("beforeunload", async () => {
      if (this.sharing) {
        await this.stopPresentation();
      }
      // Removing the spotlight when spotlighted user leaves
      if (this.spotlightPeerId === this.mediasoupId) {
        await this.roomClient.sendAllMessage("set_spotlight", {
          spotlightPeerId: null,
        });
      }
      // Setting status if hybrid user leaves
      if (this.hybridUserJoined) {
        this.$store.commit("mediasoup/set_hybridUserJoined", false);
      }
      await this.roomClient.close();
    });
  },
  computed: {
    ...mapState({
      mediasoupConfig: (state) => state.mediasoup.mediasoupConfig,
      testDataChannelOk: (state) => state.mediasoup.testDataChannelOk,
      microphones: (state) => state.mediasoup.microphones,
      cameras: (state) => state.mediasoup.cameras,
      currentMicId: (state) => state.mediasoup.currentMicId,
      currentCamId: (state) => state.mediasoup.currentCamId,
      isConference: (state) => state.mediasoup.isConference,
      isPresentation: (state) => state.mediasoup.isPresentation,
      maxParticipantsNotification: (state) => {
        return state.mediasoup.maxParticipantsNotification;
      },
      displayName: (state) => state.mediasoup.displayName,
      audioConnected: (state) => state.mediasoup.audioConnected,
      consumeLang: (state) => state.mediasoup.consumeLang,
      role: (state) => state.mediasoup.role,
      produceLang: (state) => state.mediasoup.produceLang,
      clientTranslator: (state) => state.mediasoup.clientTranslator,
      peers: (state) => state.mediasoup.peers,
      allJoinedUsers: (state) => state.mediasoup.allPeers,
      hearOtherTranslator: (state) => state.mediasoup.hearOtherTranslator,
      onAir: (state) => state.mediasoup.onAir,
      micPaused: (state) => state.mediasoup.micPaused,
      vidPaused: (state) => state.mediasoup.vidPaused,
      handRaised: (state) => state.mediasoup.handRaised,
      passiveRequestRejected: (state) => state.mediasoup.passiveRequestRejected,
      passiveRequestAccepted: (state) => state.mediasoup.passiveRequestAccepted,
      passiveRequestId: (state) => state.mediasoup.passiveRequestId,
      activeRequestId: (state) => state.mediasoup.activeRequestId,
      activeRequestAccepted: (state) => state.mediasoup.activeRequestAccepted,
      currentPresentator: (state) => state.mediasoup.currentPresentator,
      mediasoupId: (state) => state.mediasoup.mediasoupId,
      websocketReady: (state) => state.mediasoup.websocketReady,
      sharing: (state) => state.mediasoup.sharing,
      reqToSpeakOngoing: (state) => state.mediasoup.reqToSpeakOngoing,
      allowedToSpeak: (state) => state.mediasoup.allowedToSpeak,
      isSpotlight: (state) => state.mediasoup.isSpotlight,
      spotlightPeerId: (state) => state.mediasoup.spotlightPeerId,
      forceTakeover: (state) => state.mediasoup.forceTakeover,
      noTranslation: (state) => state.mediasoup.noTranslation,
      connectionType: (state) => state.mediasoup.connectionType,
      browserName: (state) => state.mediasoup.browserName,
      lastAlive: (state) => state.mediasoup.lastAlive,
      hybridUserJoined: (state) => state.mediasoup.hybridUserJoined,
      testViewer: (state) => state.mediasoup.testViewer,
      transcriptionResult: (state) => state.mediasoup.transcriptionResult,
      transcriptionStarted: (state) => state.mediasoup.transcriptionStarted,
    }),
    activeOverlay() {
      return this.role;
    },
    participants() {
      let obj = {};
      for (let participant of this.firestoreParticipants) {
        obj[participant.id] = participant;
      }
      //logger.info("participants", obj);
      return obj;
    },
    translators() {
      let obj = {};
      for (let translator of this.firestoreTranslators) {
        obj[translator.id] = translator;
      }
      return obj;
    },
    langWhiteList() {
      if (this.event) {
        return this.event.langComb
          ? this.event.langComb
              .map((lc) => [lc.lang1, lc.lang2])
              .flat()
              .filter((l, i, arr) => arr.indexOf(l) == i)
          : [];
      } else {
        return [];
      }
    },
    activeTranslator() {
      return this.activeTranslatorUid ? this.translators[this.activeTranslatorUid] : null;
    },
    activeTranslation() {
      let active = Object.values(this.peers).filter(
        (t) =>
          this.$store.getters.getCurrentUser &&
          this.$store.getters.getCurrentUser.uid != t.id &&
          t.onAir &&
          t.produceLang.toUpperCase() === this.consumeLang,
      );
      if (active.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    /**
     * Returns the current onAir interpreter from booth partners.
     * @public
     */
    activeOnAir() {
      logger.info("active booth partners", this.boothPartners);
      for (let t of this.boothPartners) {
        if (t.onAir) {
          return t;
        }
      }
      return null;
    },

    forceTakeoverOpen() {
      //Detect if no one is tranlating in current lang comb - rausgefallen auf Kundenwunsch 08.06.21
      /*
        if(this.activeOnAir === null && this.translatorReady && !this.onAir){
          return true
        }else */

      if (this.doForceTakeover) {
        return true;
      } else {
        return false;
      }
    },

    autoDetectForceTakeover() {
      return false;
      //Detect if no one is tranlating in current lang comb - rausgefallen auf Kundenwunsch 08.06.21
      /* 
        if(this.forceTakeoverOpen && this.activeOnAir === null){
          return true
        }else{
          return false
        }
        */
    },
    /**
     * Computed property returns a list of booth partners from firestore.
     */
    translatorPartners() {
      if (this.isTranslator) {
        let arr = Object.values(this.translators).filter(
          (t) =>
            this.$store.getters.getCurrentUser &&
            this.$store.getters.getCurrentUser.uid != t.id &&
            ((t.lang1 == this.clientTranslator.lang1 && t.lang2 == this.clientTranslator.lang2) ||
              (t.lang1 == this.clientTranslator.lang2 && t.lang2 == this.clientTranslator.lang1)),
        );
        //filter for last Active
        return arr.filter((t) => {
          try {
            if (t.lastActive) {
              if (t.lastActive.toDate().getTime() < new Date().getTime() - 35000) {
                return false;
              }
            } else {
              return false;
            }
          } catch (err) {
            console.warn(err);
          }
          return true;
        });
      } else {
        return [];
      }
    },
    /**
     * Computed property returns a list of booth partners from Vuex store.
     */
    boothPartners() {
      let peers = this.$store.getters["mediasoup/getPeers"];
      const allInterpreters = peers.filter((peer) => {
        if (peer.role === "translator") {
          return true;
        }
      });
      return allInterpreters.filter((peer) => {
        if (
          (peer.clientTranslator.lang1 == this.clientTranslator.lang1 &&
            peer.clientTranslator.lang2 == this.clientTranslator.lang2) ||
          (peer.clientTranslator.lang1 == this.clientTranslator.lang2 &&
            peer.clientTranslator.lang2 == this.clientTranslator.lang1)
        ) {
          return true;
        }
      });
    },

    activeWillTake() {
      let req = this.translatorPartners.find((t) => t.willTake === true); //&&(Date.now() - t.lastActive.seconds*1000) <= 40000
      if (req) {
        return req;
      }
      return null;
    },
    /**
     * Computed property returns participants collection from firebase db.
     */
    participantsCollection() {
      return db.collection(
        `organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}/participants`,
      );
    },
    /**
     * Computed property returns translators collection from firebase db.
     */
    translatorsCollection() {
      return db.collection(
        `organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}/translators`,
      );
    },
  },

  watch: {
    langWhiteList: {
      immediate: true,
      handler() {
        if (this.settingsRelayLang.length == 0 && this.langWhiteList[0]) {
          this.settingsRelayLang = this.langWhiteList.slice(0, 3);
        }
      },
    },
    maxParticipantsNotification(val) {
      if (val) {
        this.$buefy.toast.open({
          message: this.$t("maxParticipantsAlert"),
          type: "is-danger",
        });
        this.$store.commit("mediasoup/set_maxParticipantsNotification", false);
      }
    },
    forceTakeover(val) {
      if (val !== null) {
        this.$buefy.toast.open({
          duration: 10000,
          message: this.forceTakeover,
          type: "is-danger",
        });
      }
      this.$store.commit("mediasoup/set_forceTakeover", null);
    },
    passiveRequestRejected(val) {
      if (val) {
        this.$buefy.toast.open({
          message: this.$t("translatorDeniedAlert"),
          type: "is-danger",
        });
        this.$store.commit("mediasoup/set_passiveRequestRejected", false);
      }
    },
    forceTakeoverOpen(val) {
      if (val) {
        this.$store.commit("mediasoup/set_hearOtherTranslator", true);
      } else {
        this.$store.commit("mediasoup/set_hearOtherTranslator", false);
      }
    },
    activeTranslation(val) {
      if (!this.noTranslation && this.event.eventType !== "hybrid" && this.role !== "proxy") {
        this.$store.commit("mediasoup/set_floor", !val);
      }
    },
    async onAir(val) {
      logger.info("onAirwatcherval", val);
      if ((!val && this.clientTranslator.requestHandover) || (!val && this.doForceTakeover)) {
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            onAir: false,
            requestHandover: false,
          },
          { merge: true },
        );
        this.$store.commit("mediasoup/set_requestHandover", false);
        this.setMute(true);
      }
      if (!val) {
        //for forcetakeover
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            onAir: false,
            ready: false,
          },
          { merge: true },
        );
        this.setMute(true);
        this.$store.commit("mediasoup/set_translatorReady", false);
      }
    },
    async websocketReady(val) {
      logger.info("  websocketReady ~ val", val);

      if (val) {
        this.joinRoom();
      }
    },
  },

  async created() {
    if (this.$route.query.lang) {
      this.$store.commit("mediasoup/set_consumeLang", this.$route.query.lang.toUpperCase());
    }

    // setting translators initial consume lang to null
    // so that he can hear everybody in floors
    if (this.isTranslator) {
      this.$store.commit("mediasoup/set_consumeLang", null);
    }
    let connectionType;
    if (this.browserName === "firefox" || this.browserName === "safari" || this.browserName === "ios") {
      connectionType = "";
    } else {
      connectionType = navigator.connection.effectiveType;
    }

    (this.isModerator = this.$route.query.isModerator === "true"),
      (this.roomClient = new RoomClient({
        mediasoupConfig: this.mediasoupConfig,
        roomId: this.$route.query.roomId || this.$route.params.eventId,
        peerId: this.mediasoupId,
        displayName: this.displayName || this.$route.query.nick,
        device: deviceInfo(),
        role: this.role || "host",
        isFloor: this.role === "host",
        handlerName: this.$route.query.handler,
        useSimulcast: false,
        useSharingSimulcast: this.$route.query.sharingSimulcast !== "false",
        forceTcp: this.$route.query.forceTcp === "true",
        produce: this.$route.query.produce !== "false", //Question currentUser.role.produce
        consume: true, //Question currentUser.role.produce
        forceH264: this.$route.query.forceH264 === "true",
        forceVP9: this.$route.query.forceVP9 === "true",
        svc: this.$route.query.svc,
        externalVideo: this.$route.query.externalVideo === "true",
        e2eKey: this.$route.query.e2eKey,
        produceLang: this.produceLang,
        clientTranslator: this.clientTranslator,
        consumeLang: this.consumeLang,
        eventId: this.$route.params.eventId,
        isModerator: this.isModerator,
        connectionType: connectionType, // gets users connection strength
        allowedToSpeak: this.allowedToSpeak,
        testViewer: this.testViewer,
      }));
    //connect Websocket
    this.roomClient._connect();

    setInterval(() => {
      this.updateActive();
      this.setMaxSendingSpatialLayer();
    }, 30000);
  },

  async beforeDestroy() {
    await this.roomClient.close();
  },

  methods: {
    /**
     * Opens feedback snackbar
     */
    // openFeedbackSnackbar() {
    //   this.$buefy.snackbar.open({
    //     type: "is-primary",
    //     duration: 10000,
    //     message: "Like Qonda? Rate us here",
    //     position: "is-bottom-right",
    //     cancelText: "Close",
    //     actionText: "feedback page",
    //     onAction: () => {
    //       window.open("https://www.provenexpert.com/qonda/d6tn/", "_blank");
    //     }
    //   });
    // },
    /**
     * Checks the media producing users count and
     * sets the spatial layer accordingly
     */
    setMaxSendingSpatialLayer() {
      const mediaProducingUsers = this.allJoinedUsers.filter(
        (peer) => peer.role !== "listener" && peer.role !== "translator" && peer.role !== "viewer",
      );
      if (mediaProducingUsers.length <= 4) {
        this.roomClient.setMaxSendingSpatialLayer(2);
      } else if (mediaProducingUsers.length <= 20) {
        this.roomClient.setMaxSendingSpatialLayer(1);
      } else if (mediaProducingUsers.length > 20) {
        this.roomClient.setMaxSendingSpatialLayer(0);
      }
    },
    /**
     * Changes the consume language of the user
     * @public
     */
    changeConsumeLang(val) {
      // Null check
      if (val) {
        this.$store.commit("mediasoup/set_consumeLang", val.toUpperCase());
      } else {
        this.$store.commit("mediasoup/set_consumeLang", val);
      }
      this.roomClient.sendConsumeLangChngMsgToAll(val);
    },
    /**
     * Leave the conference
     * @public
     */
    leave() {
      this.roomClient.close();
      window.location.href = "https://www.provenexpert.com/qonda/d6tn/";
      //this.roomClient.sendAllMessage('server_close',{})
      //this.roomClient.closeWebsocket()
      //this.startPresentation()
      //this.roomClient.changeWebcamResolution()
    },
    /**
     * For testing - reduce network speed
     * @public
     */
    async applyNetworkThrottle() {
      // applying network throttle for testing
      const payload = {
        uplink: 800000,
        downlink: 800000,
        rtt: 400,
      };
      await this.roomClient.applyNetworkThrottle(payload);
    },
    /**
     * For testing - reset network speed
     * @public
     */
    async resetNetworkThrottle() {
      await this.roomClient.resetNetworkThrottle();
    },
    /**
     * checks if user is allow to enter (participant limit) then joins
     *
     * @public
     */
    async joinRoom() {
      if (await this.roomClient.canIJoin(this.role)) {
        this.roomActive = true;
        await this.roomClient.join();
      } else {
        this.$buefy.toast.open({
          message: this.$t("roomFullAlert"),
          type: "is-danger",
          duration: "60000",
        });
        const peersList = this.$store.getters["mediasoup/getPeers"];
        logger.info("peers list", peersList);
        if (peersList.length > 0) {
          logger.info("Informing moderator..");
          peersList.forEach((peer) => {
            this.roomClient.sendPeerMessage("max_participants", peer.id, {});
          });
        }
      }
      //Video Mute settings in roomclient-enable webcam/mic
    },
    /**
     * Set video on/off
     * @public
     */
    async videoMute(state) {
      logger.info("videoMute");
      if (state) {
        await this.roomClient.videoMute();
      } else {
        await this.roomClient.videoUnMute();
      }
    },
    /**
     * toggle video on/off
     * @public
     */
    async toggleVideoMute() {
      logger.info("togglevideoMute");
      if (this.vidPaused) {
        await this.roomClient.videoUnMute();
      } else {
        await this.roomClient.videoMute();
      }
    },
    /**
     * toggle mic on/off
     * @public
     */
    async toggleMute() {
      logger.info("togglemute");
      // Going onAir from passive request
      if (this.micPaused && this.isTranslator && this.passiveRequestAccepted) {
        logger.info("Finishing passive");
        logger.info("passiveRequestId", this.passiveRequestId);
        this.finishHandover(this.activeOnAir?.id);
        this.$store.commit("mediasoup/set_passiveRequestAccepted", false);
        this.$store.commit("mediasoup/set_hearOtherTranslator", false);
      } else if (this.micPaused && this.isTranslator && this.activeRequestAccepted) {
        logger.info(`File Room.vue: activeOnAir`, this.activeOnAir);
        this.finishHandover(this.activeOnAir?.id);
        this.$store.commit("mediasoup/set_activeRequestAccepted", false);
        this.$store.commit("mediasoup/set_hearOtherTranslator", false);
      } else if (this.micPaused && this.isTranslator && this.activeOnAir) {
        this.$buefy.toast.open({
          message: this.$t("alreadyActiveOnAir"),
          type: "is-danger",
        });
        return;
      } else if (this.micPaused) {
        await this.roomClient.unmuteMic();
        if (this.isTranslator) {
          logger.info("setonairtrue");
          this.setOnAir(true);
        }
      } else {
        await this.roomClient.muteMic();
        if (this.isTranslator) {
          logger.info("onAirFalse");
          this.setOnAir(false);
        }
      }
    },
    /**
     * Set mic on/off
     * @public
     */
    setMute(muted) {
      if (muted) {
        logger.info("setMuteTrue");

        this.roomClient.muteMic();
      } else {
        this.roomClient.unmuteMic();
      }
    },
    /**
     * Set this translator onAir status
     * @public
     */
    async setOnAir(onAir) {
      await this.roomClient.setOnAir(onAir);
      this.$store.commit("mediasoup/set_onAir", onAir);
      await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
        {
          onAir: onAir,
        },
        { merge: true },
      );
    },
    /**
     * toggle screenshare on/off
     * @public
     */
    async toggleScreen() {
      logger.info("toggleScreen");
      if (this.role === "host") {
        if (this.currentPresentator === this.mediasoupId || this.currentPresentator === null) {
          if (this.sharing) {
            this.stopPresentation();
            this.$store.commit("mediasoup/set_sharing", false);
          } else {
            this.startPresentation();
          }
        } else {
          this.$buefy.toast.open({
            message: this.$t("someonePresentingAlert"),
            type: "is-danger",
          });
        }
      } else {
        this.$buefy.toast.open({
          message: this.$t("onlyModeratorCanStartAlert"),
          type: "is-danger",
        });
      }
    },
    /**
     * Start presentation
     * @public
     */
    async startPresentation() {
      await this.roomClient.enableShare();
    },
    /**
     * Stop presentation
     * @public
     */
    async stopPresentation() {
      await this.roomClient.disableShare();
      await this.roomClient.videoUnMute();
      this.roomClient.stopPresentation();
    },
    /**
     * Set spotlight on a user
     * @public
     */
    async setSpotlight(mediasoupId) {
      logger.info("set spotlight: ", mediasoupId);

      await this.roomClient.setSpotlight(mediasoupId);
    },
    /**
     * Moderator function for putting down a raised hand
     * @public
     */
    async disableRaiseHand(uid, mediasoupId) {
      logger.info("disableRaiseHand", uid, mediasoupId);

      // logger.info(await this.participantsCollection.doc(uid).get().data());
      this.participantsCollection.doc(uid).set(
        {
          handRaised: false,
        },
        { merge: true },
      );
      // logger.info(await this.participantsCollection.doc(uid).get().data());
      this.roomClient.sendAllMessage("set_hand_raised", {
        id: mediasoupId,
        handRaised: false,
      });
    },
    /**
     * Toggle hand raise
     * @public
     */
    async toggleRaiseHand() {
      logger.info("toggleRaiseHand");
      if (this.handRaised) {
        // setting the handRaised status for others
        this.roomClient.sendAllMessage("set_hand_raised", {
          id: this.mediasoupId,
          handRaised: false,
        });
        // setting the handraised for myself
        this.$store.commit("mediasoup/set_handRaised", {
          id: this.mediasoupId,
          handRaised: false,
        });
        this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            handRaised: false,
          },
          { merge: true },
        );
      } else {
        // setting the handRaised status for others

        this.roomClient.sendAllMessage("set_hand_raised", {
          id: this.mediasoupId,
          handRaised: true,
        });
        // setting the handraised for myself
        this.$store.commit("mediasoup/set_handRaised", {
          id: this.mediasoupId,
          handRaised: true,
        });
        this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            handRaised: true,
          },
          { merge: true },
        );
      }
    },
    /**
     * Set translator ready status
     * @public
     */
    translatorIsReady() {
      logger.info("translatorisready");
      this.$store.commit("mediasoup/set_translatorReady", true);
      this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
        {
          ready: true,
        },
        { merge: true },
      );
    },
    /**
     * Active interpreter requests a handover to all booth partners.
     * @public
     */
    requestActiveHandover() {
      logger.info("booth partners", this.boothPartners);
      // Checking whether booth partners are available
      if (this.boothPartners.length == 0) {
        this.$buefy.toast.open({
          message: this.$t("noPartner"),
          type: "is-danger",
        });
        return;
      } else {
        this.boothPartners.forEach((t) => {
          this.roomClient.sendActiveHandoverRequest(t.id);
        });
      }
    },
    /**
     * Handler for accept active request.
     * @public
     */
    acceptActiveRequest() {
      this.$store.commit("mediasoup/set_activeRequestAccepted", true);
      this.$store.commit("mediasoup/set_translatorReady", true);
      this.$store.commit("mediasoup/set_hearOtherTranslator", true);
      this.roomClient.sendPeerMessage("active_request_accepted", this.activeRequestId, {
        answererId: this.mediasoupId,
        answererName: this.displayName,
      });
    },
    /**
     * Handler for reject active request.
     * @public
     */
    rejectActiveRequest() {
      logger.info("request rejected");
      this.$store.commit("mediasoup/set_activeRequestAccepted", false);
      this.roomClient.sendPeerMessage("active_request_rejected", this.activeRequestId, {
        answererId: this.mediasoupId,
        answererName: this.displayName,
      });
    },
    /**
     * Notify passive interpreter that handover request has been
     * accepted by the active/onAir interpreter.
     * @public
     */
    notifyHandoverAccepted() {
      this.roomClient.sendPeerMessage("passive_request_accepted", this.passiveRequestId);
    },
    /**
     * Sends a passive handover request to the active interpreter.
     * @public
     */
    async requestPassiveHandover() {
      if (this.isTranslator && this.clientTranslator.translatorReady === true) {
        // Checking whether booth partners are available
        if (this.boothPartners.length == 0) {
          this.$buefy.toast.open({
            message: this.$t("noPartner"),
            type: "is-danger",
          });
          return;
        }
        if (!this.onAir) {
          this.$buefy.toast.open({
            message: this.$t("handoverPossibleAlert"),
            type: "is-danger",
          });

          this.roomClient.sendPassiveHandoverRequest(this.activeOnAir.id);
          return;
        }
        this.$store.commit("mediasoup/set_requestHandover", true);
        this.$store.commit("mediasoup/set_openPassiveHandoverNotification", false);
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            requestHandover: this.clientTranslator.requestHandover,
          },
          { merge: true },
        );
      } else {
        return;
      }
    },
    /**
     * Active translator denies handover request from passive translator
     * @public
     */
    async rejectPassiveRequest() {
      logger.info("Rejecting handover Request", this.passiveRequestId);
      this.roomClient.sendPeerMessage("passive_request_rejected", this.passiveRequestId);
      this.$store.commit("mediasoup/set_passiveRequestId", null);
      this.$store.commit("mediasoup/set_openPassiveHandoverNotification", false);
    },
    /**
     * finish handover process
     * @public
     */
    async finishHandover(activeOnAirId) {
      this.$store.commit("mediasoup/set_translatorReady", true);
      this.setOnAir(true);
      this.setMute(false);
      if (activeOnAirId) {
        this.roomClient.setPartnerOffAir(activeOnAirId, false);
      }
    },
    /**
     * toggle whether floor should be hearable
     * @public
     */
    toggleFloor() {
      logger.info("switchToFloor");
      this.$store.commit("mediasoup/toggle_floor");
    },
    /**
     * toggle current outputlang of translator
     * @public
     */
    async toggleCurrentOutputLang() {
      let ct = this.clientTranslator;
      let nextLang = "";
      if (ct.lang1 === ct.currentOutputLang) {
        nextLang = ct.lang2;
      } else {
        nextLang = ct.lang1;
      }
      ct.currentOutputLang = nextLang;
      this.$store.commit("mediasoup/set_clientTranslator", ct);
      this.$store.commit("mediasoup/set_produce_lang", nextLang.toUpperCase());
      this.roomClient.setProduceLang(nextLang);
      await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
        {
          currentOutputLang: ct.currentOutputLang,
        },
        { merge: true },
      );
    },
    /**
     * toggle the eavesdropping function for the other translator
     * @public
     */
    toggleHearOtherTranslator() {
      logger.info("togglehearothertranslator");
      this.$store.commit("mediasoup/toggle_other_translator");
    },
    /**
     * finish takeover process
     * @public
     */
    finishTakeover(id, forced) {
      //this.setOnAir(true)
      this.roomClient.setPartnerOffAir(id, forced);
    },
    /**
     * start forcetakeover
     * @public
     */ startForceTakeover() {
      logger.info("doForceTakeover");
      this.doForceTakeover = true;
      /// mute other translator confirmtakeover
    },
    /**
     * confirm that you really want to force takover
     * @public
     */
    confirmForceTakeover() {
      logger.info("confirmForceTakeover");
      this.setMute(false);
      this.setOnAir(true);
      this.$store.commit("mediasoup/set_translatorReady", true);
      if (this.activeOnAir) {
        logger.info("activeOnAir", this.activeOnAir);
        this.finishTakeover(this.activeOnAir.id, true);
      }
      this.doForceTakeover = false;
    },
    /**
     * abort force takeover process
     * @public
     */
    abortForceTakeover() {
      logger.info("abortForceTakeover");
      this.doForceTakeover = false;
    },
    /**
     * updates firebase with actual timestamps
     * @public
     */
    async updateActive() {
      if (this.$store.getters.getCurrentUser) {
        if (this.isTranslator) {
          await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
            {
              lastActive: FieldValue.serverTimestamp(),
            },
            { merge: true },
          );
        } else {
          await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).delete();
          await this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
            {
              lastActive: FieldValue.serverTimestamp(),
            },
            { merge: true },
          );
        }
      }
    },
    /**
     * Moderator function to mute users mic
     * @public
     */
    async muteUser(id) {
      if (this.isModerator) {
        this.roomClient.muteRemotePeer(id);
      }
    },
    /**
     * Moderator function to disable users vid
     * @public
     */
    async muteVidUser(id) {
      if (this.isModerator) {
        this.roomClient.muteRemoteVideo(id);
      }
    },
    /**
     * Moderator function to kick user
     * @public
     */
    async kickUser(id) {
      if (this.isModerator) {
        // Removing spotlight when spotlighted user is kicked
        if (this.spotlightPeerId === id) {
          this.$store.commit("mediasoup/remove_spotlight");
          this.$store.commit("mediasoup/set_spotlight_peerId", null);
          await this.roomClient.sendAllMessage("set_spotlight", {
            spotlightPeerId: null,
          });
        }
        // Get back to conference if screen shared user kicked
        if (this.isPresentation) {
          this.$store.commit("mediasoup/stop_presentation");
          await this.roomClient.sendAllMessage("presentation_stopped", {
            presenterId: id,
          });
        }
        // Kicking the user out
        await this.roomClient.kickUser(id);
      }
    },
    /**
     * change Microphone
     * @public
     */
    async changeMic(id) {
      logger.info(" ~ file: Room.vue ~ line 716 ~ changeMic ~ id", id);
      this.roomClient.changeMic(id);
    },
    /**
     * change webcam
     * @public
     */
    async changeWebcam(id) {
      logger.info(" ~ file: Room.vue ~ line 720 ~ changeWebcam ~ id", id);
      this.roomClient.changeWebcam(id);
    },
    /**
     * Viewer requests to become a host
     * @public
     */
    requestSwitchToHost() {
      if (!this.reqToSpeakOngoing) {
        // setting it for me
        this.$store.commit("mediasoup/set_reqToSpeakOngoing", {
          requesterId: this.mediasoupId,
          reqToSpeakOngoing: true,
        });
        // setting it for all others
        this.roomClient.sendAllMessage("request_to_speak_ongoing", {
          requesterId: this.mediasoupId,
          reqToSpeakOngoing: true,
        });
      } else {
        // setting it for me
        this.$store.commit("mediasoup/set_reqToSpeakOngoing", false);
        // setting it for all others
        this.roomClient.sendAllMessage("request_to_speak_ongoing", {
          requesterId: this.mediasoupId,
          reqToSpeakOngoing: false,
        });
      }
    },
    /**
     * Viewer becomes a host
     * @public
     */
    async switchToHost() {
      logger.info("switchtohost", this.mediasoupId);
      this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
        {
          reqSwitchToHost: false,
        },
        { merge: true },
      );
      this.roomClient.sendAllMessage("switched_toHost", {
        newHostId: this.mediasoupId,
      });
      this.$store.commit("mediasoup/set_role", "host");
      this.setMute(false);
      this.toggleVideoMute();
    },
    /**
     * Moderator function to allow viewer to host
     * @public
     */
    async allowSwitchToHost(viewerId) {
      if (this.isModerator) {
        this.$store.commit("mediasoup/set_allowedToSpeak", {
          viewerId: viewerId,
          allowedToSpeak: true,
        });
        // setting it for everybody
        this.roomClient.sendAllMessage("allowed_switchToHost", {
          viewerId: viewerId,
          allowedToSpeak: true,
        });
      }
    },
    /**
     * Moderator function to switch a host back to viewer
     * @public
     */
    async switchToViewer(mediasoupId) {
      // setting it in my store
      this.$store.commit("mediasoup/set_peer_role", {
        id: mediasoupId,
        role: "viewer",
      });
      this.$store.commit("mediasoup/set_allowedToSpeak", {
        viewerId: mediasoupId,
        allowedToSpeak: false,
      });
      this.$store.commit("mediasoup/set_reqToSpeakOngoing", {
        requesterId: mediasoupId,
        reqToSpeakOngoing: false,
      });
      // setting it in other's store
      this.roomClient.sendAllMessage("switch_to_viewer", {
        newViewerId: mediasoupId,
      });
    },
    /**
     * for testing - toggles webcam resultions
     * @public
     */
    async toggleResolution() {
      //this.$store.commit('mediasoup/set_role', 'viewer')
      this.roomClient.sendAllMessage("toggle_resolution");
    },
  },

  firestore() {
    return {
      firestoreParticipants: this.participantsCollection,
      firestoreTranslators: this.translatorsCollection,
      event: db.doc(`organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}`),
    };
  },
};
</script>

<style>
.card.card-overlay {
  position: fixed;
  max-width: 750px;
  width: 100%;
  padding: 2em 1em;
  border-radius: 16px;
  margin-left: 50%;
  margin-top: calc(50vh - 60px);
  transform: translate(-50%, -50%);
  z-index: 60000;
}
.bg-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 2em 1em;
  background: #121c2e;
  z-index: 59999;
}

.handover-texts {
  margin-top: 20px;
  align-items: center;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 18px;
  color: #333333;
  height: 50px;
  display: flex;
  align-items: center;
}

button.button.on-air {
  height: 50px;
  padding-left: 1.2em;
  padding-right: 1.2em;
  border: 2px solid #e5e5e5;
  color: #e5e5e5;
  font-weight: bold;
}

button.button.go-onair {
  height: 50px;
  padding-left: 1.2em;
  padding-right: 1.2em;
  border: 2px solid #6e8a14;
  background: #f2f2f2;
  font-weight: bold;
}

button.button.ready {
  height: 50px;
  padding-left: 1.2em;
  padding-right: 1.2em;
  border: 2px solid #6e8a14;
  background: #ccff23;
  font-weight: bold;
}

button.button.on-air.active {
  color: #ff0082;
  border-color: #ff0082;
}

.under-btn-text {
  margin-top: 7px;
  color: #cccccc;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  line-height: 14px;
  text-align: center;
}

.over-btn-text {
  margin-bottom: 5px;
  color: #cccccc;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  line-height: 14px;
  text-align: center;
}

.button.is-bubblegum,
.button.is-bubblegum:hover {
  background-color: #ff0082 !important;
  color: #fff;
}

.button.is-apple,
.button.is-apple:hover {
  background-color: #ccff23 !important;
  color: #fff;
}

.button.no-br-right {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.button.no-br-left {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.button.is-light,
fieldset .button.is-light {
  background-color: #f2f2f2;
  border-color: #cccccc;
  box-shadow: none;
}
.button.is-light.is-disabled,
fieldset.is-disabled .button.is-light {
  background-color: #fafafa;
  border-color: #ebebeb;
  box-shadow: none;
}

.current-translator {
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: auto;
  padding: 8px 16px;
  border-top-right-radius: 12px;
  z-index: 60000;
}

.test-img {
  position: absolute;
  top: 16px;
  left: 16px;
  width: 200px;
  height: auto;
  max-width: 25%;
  z-index: 99999999;
}

.test-img > img {
  width: 100%;
}

@media screen and (max-width: 500px) {
  .bottom-bar.level {
    text-align: center;
  }

  .bottom-bar.level > .level-item {
    display: inline-block;
  }

  .bottom-bar.level > .level-item .ml-6 {
    margin-left: 0 !important;
  }

  .bottom-bar.level > .level-item .mr-6 {
    margin-right: 0 !important;
  }
}

.transcription {
  z-index: 99999;
  margin-left: auto;
  position: absolute;
  bottom: 80px;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 2px;
  padding: 0 5px 0 5px;
}

.top-view-container {
  display: flex;
  height: calc(100vh - 0px);
  position: relative;
  width: calc(100% - 64px - 1rem);
  justify-content: center;
  align-content: center;
}
.top-view-container.small {
  width: calc(80% - 1rem);
}
.top-view-container.translator-view {
  height: calc(100vh - 15rem);
}
.conference-container,
.presentation-container,
.spotlight-container {
  display: flex;
  justify-content: center;
  align-content: center;
  width: 100%;
  height: calc(100% - 76px - 1rem);
  border: 10px solid transparent;
}
.translator-view .conference-container,
.translator-view .presentation-container,
.translator-view .spotlight-container {
  height: 100%;
}

.remote-users {
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  width: 100%;
  /*overflow: auto; */
  position: relative;
  justify-content: center;
  align-content: center;
}

.remote-user {
  position: relative;
}

.stream-id {
  position: absolute;
  bottom: 0;
  max-width: (100% - 24px);
  color: #fff;
  z-index: 3;
  padding: 24px;
  font-size: 15px;
  letter-spacing: 0.5px;
  line-height: 22.5px;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
}

.remote-user.adaptive {
  height: 120px !important;
  width: 160px;
  min-width: 160px;
  margin-top: calc(100vh - 120px - 32px);
  justify-content: center;
  display: flex;
}

.remote-user.adaptive.active-speaker.isTranslator {
  height: calc(100% - 240px - 32px - 32px) !important;
}

.remote-user.active-speaker {
  /*transition: transform 1s ease;*/
  z-index: 1;
}

.remote-user.active-speaker.big {
  transform: translateX(-180px);
}

.remote-user.active-speaker:not(.big) {
  transform: translateX(-32px);
}

.remote-user.adaptive.active-double {
  border: 2px solid #ccff23;
  border-radius: 4px;
}

.remote-user.adaptive.isTranslator {
  margin-top: calc(100vh - 240px - 32px - 32px);
}
.remote-user.active {
  border: turquoise;
  border-style: solid;
}

.remote-user.active-speaker:not(.adaptive) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  margin-top: 0 !important;
  height: 100% !important;
}

.remote-user.adaptive.active-speaker {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  margin-top: 0 !important;
  height: calc(100% - 120px - 32px) !important;
}

.remote-user.adaptive.active-speaker .bottom-left {
  display: none;
}

.remote-user.adaptive:not(.active-speaker) .bottom-left {
  position: absolute;
  bottom: 8px;
  left: 12px;
  color: #ff0082;
  z-index: 6;
}
.remote-user.adaptive.active-speaker .bottom-right {
  display: none;
}

.remote-user.adaptive:not(.active-speaker) .bottom-right {
  position: absolute;
  bottom: 8px;
  right: 12px;
  color: rgba(255, 255, 255, 0.5);
  z-index: 12;
  cursor: pointer;
}

.remote-user.adaptive:not(.active-speaker) {
  margin-bottom: 32px;
  flex-grow: 0;
  margin-left: 12px;
  margin-right: 12px;
}

.remote-user.only-sound.active-speaker .remote-user__robert-container {
  width: 100%;
  max-width: 1100px;
  border: 2px solid #fff;
}

.remote-user.offline.active-speaker .remote-user__robert-container {
  width: 100%;
  max-width: 1100px;
  border: 2px solid #fff;
  opacity: 0.4;
}
.remote-user.adaptive.offline:not(.active-speaker) .remote-user__robert-container {
  width: 160px;
  border: 2px solid #fff;
  opacity: 0.4;
}

.remote-user.adaptive.only-sound:not(.active-speaker) .remote-user__robert-container {
  width: 160px;
  border: 2px solid #fff;
}

.remote-user.adaptive:not(.active-speaker) .remote-user__robert-container {
  max-width: 160px;
}

.remote-user.adaptive:not(.active-speaker) .stream-id {
  position: absolute;
  padding: 12px 0 0 12px;
  top: 0;
  left: 0;
  font-size: 12px;
  line-height: 12px;
  text-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
}

.remote-user.adaptive:not(.active-speaker) .stream-id .user-name {
  font-weight: bold;
}

.remote-user__video-container {
  height: 100%;
  display: inline-block;
}

.remote-user__robert-container {
  display: inline-flex;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.remote-user.adaptive.active-speaker .remote-user__robert-container {
  height: calc(100% - 64px);
  margin: 32px auto;
}

.remote-user.active-speaker:not(.adaptive) .remote-user__robert-container {
  height: calc(100% - 64px);
  left: 50%;
  top: 32px;
  transform: translateX(-50%);
}

.qonda-watermark {
  width: 100px;
  height: auto;
  z-index: 99999;
  opacity: 0.5;
  margin: 2rem;
  position: absolute;
  left: 0;
  bottom: 0;
}

/* BEGIN WAVEFORM */
.wave-form {
  position: absolute;
  height: 30px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.remote-user.active-speaker .wave-form {
  transform: translate(-50%, -50%) scale(4);
}

.loader-container {
  position: relative;
  height: 30px;
  width: 160px;
  text-align: center;
}

.loader-container > div {
  position: relative;
  display: inline-block;
  background: #fff;
  height: 1px;
  width: 1px;
  border-radius: 20px;
  margin: 0 1px;
}

.loader-container.play > div {
  -webkit-animation: load 3s ease-in-out infinite;
  animation: load 3s ease-in-out infinite;
}

.loader-container.play .rectangle-2 {
  -webkit-animation-delay: 0.6s;
  animation-delay: 0.6s;
}

.loader-container.play .rectangle-3 {
  -webkit-animation-delay: 0.3s;
  animation-delay: 0.3s;
}

.loader-container.play .rectangle-4 {
  -webkit-animation-delay: 0.1s;
  animation-delay: 0.1s;
}

.loader-container.play .rectangle-5 {
  -webkit-animation-delay: 0.7s;
  animation-delay: 0.7s;
}

.loader-container.play .rectangle-6 {
  -webkit-animation-delay: 0.7s;
  animation-delay: 0.7s;
}

@keyframes load {
  0%,
  100% {
    transform: scaleY(1);
  }
  16.67% {
    transform: scaleY(30);
  }
  33.33% {
    transform: scaleY(6);
  }
  50% {
    transform: scaleY(30);
  }
  66.67% {
    transform: scaleY(1);
  }
  83.34% {
    transform: scaleY(30);
  }
}

/* END WAVEFORM */

@media screen and (max-width: 640px) {
  .top-view-container {
    flex-wrap: wrap;
    width: calc(100% - 64px);
  }

  .top-view-container .qonda-watermark {
    width: 100px;
    height: 50px;
    opacity: 0.5;
    margin: 1rem;
    position: fixed;
    left: 0;
    top: 0;
  }

  .chat-container.card.sidebar {
    min-width: 360px;
    height: 100vh;
    position: fixed;
    right: 0px;
  }

  .chat-container {
    height: auto;
    max-height: 100%;
    z-index: 300;
  }

  .card.card-overlay {
    position: fixed;
    max-width: 750px;
    width: 100%;
    padding: 2em 1em;
    border-radius: 16px;
    margin-left: 50%;
    margin-top: calc(50vh - 60px);
    transform: translate(-50%, -50%);
    z-index: 60000;
  }

  .remote-users {
    max-height: 50vh;
  }

  .remote-user.active-speaker:not(.adaptive) .remote-user__robert-container {
    transform: translateX(0) !important;
    max-width: 100% !important;
    left: 0 !important;
  }

  .overlay-buttons {
    flex-direction: row;
    position: fixed;
    width: 100%;
    display: flex;
  }

  .overlay-buttons .field.is-grouped-centered.is-grouped {
    max-width: 320px;
    flex-wrap: wrap;
    margin-right: 64px;
  }

  /* .overlay-buttons .field.is-grouped-centered.is-grouped button {
    margin: 0.5rem !important;
  } */
  /* .button-bar {
    width: 100%;
    display: flex;
    bottom: auto;
    left: 0;
    height: 80px;
    z-index: 100000;
    background-color: rgb(43, 43, 43);
  } */

  .remote-user.only-sound.active-speaker.big {
    transform: translateX(0px);
  }

  .remote-users .remote-user.active-speaker {
    transform: translateX(0px) !important;
    height: calc(50vh - 120px - 32px) !important;
  }
}
</style>

<style>
.v-step {
  z-index: 70000 !important;
  max-width: 380px !important;
}

.chat-container.card.sidebar {
  min-width: 360px;
  height: 100vh;
  position: fixed;
  right: 0px;
}

.chat-container.card.sidebar.translator {
  height: calc(100vh - 15rem);
}

.local-track-container > :not(:last-child) {
  display: none !important;
}

button.button.on-air.user-list {
  margin: 0;
  padding: 0.5em;
  height: 36px;
}

.remote-user__video-container > div {
  width: auto !important;
  display: inline-block !important;
}

.remote-user__video-container video {
  display: inline-block !important;
  width: auto !important;
  position: relative !important;
}

.trans-btn-group > .label {
  color: #333333;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  line-height: 14px;
  text-align: center;
}

.device-settings-container .control,
.device-settings-container .b-tooltip {
  max-width: 100%;
}

.agora_video_player {
  object-fit: contain !important;
  background: #121c2e;
}

.local-video-test {
  width: 50%;
  height: auto;
  max-width: 100%;
  margin-top: 1em;
}

.local-video-test .agora_video_player {
  background: #fff;
}

.device-settings-container {
  display: flex;
  width: 100%;
}

.device-settings {
  width: 375px;
  padding: 1em;
  height: auto;
}

.local-track-container {
  width: fit-content;
  height: fit-content;
  /*position: absolute;*/
  right: 16px;
  top: calc(100vh - 240px - 16px);
  background: transparent;
  opacity: 0.7;
  transition-delay: 2s;
  transition: opacity 0.5s ease 2s;
  z-index: 6000;
  border-radius: 4px;
  overflow: hidden;
}

.local-track-container > div {
  background: #121c2e !important;
  width: auto !important;
  display: inline-block !important;
}

.local-track-container video {
  object-position: bottom left;
  /*width: auto!important;*/
  display: inline-block !important;
  position: relative !important;
}

.local-track-container:hover {
  transition-delay: 0s;
  opacity: 1;
}
.notices {
  z-index: 70000;
}
.testmove {
  display: block;
  position: absolute;
  bottom: 0;
  right: 0;
  height: 150px;
  width: 150px;
  margin: 0px;
  background: transparent;
  color: white;
  z-index: 70000;
}

@media screen and (max-width: 640px) {
  .local-track-container {
    width: 100px;
    height: 60px;
  }
}
</style>
